import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { VenetianMask, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Velvet Stranger", "Shadow", "Mister Hush", "Anon Bear", "Late Night",
  "Whisper", "Eclipse", "After Hours", "The Visitor", "Tall Dark", "Mystery", "Quiet Storm",
];

export default function MaskSheet({ open, onClose, roomId, currentMask, onUpdate }) {
  const [alias, setAlias] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setAlias(currentMask?.alias || "");
  }, [open, currentMask]);

  const enableMask = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/mask`, {
        enabled: true,
        alias: alias.trim() || null,
      });
      toast.success(`Hidden as ${data.alias}`);
      onUpdate?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const disableMask = async () => {
    setSubmitting(true);
    try {
      await api.post(`/rooms/${roomId}/mask`, { enabled: false });
      toast.success("Mask removed");
      onUpdate?.({ enabled: false });
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isOn = !!currentMask?.enabled;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0b0b0e] border-white/10 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <VenetianMask className="w-5 h-5 text-fuchsia-400" />
            {isOn ? "You're incognito" : "Slip on a mask"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            DownLow only. Your real handle, avatar, and location stay hidden.
          </DialogDescription>
        </DialogHeader>

        {isOn ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-4">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-semibold">
                Your alias
              </div>
              <div className="font-display text-2xl font-bold mt-1">
                {currentMask?.alias || "Anon"}
              </div>
            </div>
            <button
              onClick={disableMask}
              disabled={submitting}
              data-testid="mask-disable-btn"
              className="ghost-btn w-full"
            >
              {submitting ? "Removing…" : "Remove mask"}
            </button>
          </div>
        ) : (
          <form onSubmit={enableMask} className="space-y-4 mt-1">
            <input
              data-testid="mask-alias-input"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Pick a name (or leave blank)"
              maxLength={24}
              className="text-input"
            />
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.slice(0, 6).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setAlias(s)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-300"
                >
                  <Sparkles className="w-3 h-3 inline mr-1 text-fuchsia-400" />
                  {s}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="mask-enable-btn"
              className="neon-btn w-full"
            >
              {submitting ? "Slipping in…" : "Go incognito"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
